import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { CustomStatusService } from '../../../../core/services/custom-status.service';
import { CustomStatus, MetaStatus, StatusType } from '../../../../core/models/models';
import { StatusDialogComponent } from '../dialog/status-dialog.component';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-status-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatDialogModule, MatPaginatorModule],
  templateUrl: './status-list.component.html',
  styleUrl: './status-list.component.scss'
})
export class StatusListComponent implements OnInit {
  statuses: CustomStatus[] = [];
  errorMessage: string = '';
  successMessage: string = '';
  metaStatusOptions = Object.values(MetaStatus);
  statusTypeOptions: Array<StatusType | 'ALL'> = ['ALL', ...Object.values(StatusType)];
  metaStatusFilter: MetaStatus | 'ALL' = 'ALL';
  typeFilter: StatusType | 'ALL' = 'ALL';
  showArchived = false;
  searchQuery = '';
  page = signal(0);
  size = signal(10);
  totalElements = signal(0);
  sortField = 'name';
  sortDirection = 'asc';

  private searchSubject = new Subject<string>();

  constructor(
    private customStatusService: CustomStatusService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadStatuses();
    });
  }

  loadStatuses(): void {
    this.customStatusService
      .getPage(
        this.searchQuery,
        this.page(),
        this.size(),
        `${this.sortField},${this.sortDirection}`,
        this.showArchived,
        this.metaStatusFilter,
        this.typeFilter
      )
      .subscribe({
      next: (data) => {
        this.statuses = data.content;
        this.totalElements.set(data.totalElements);
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement des statuts';
        this.cdr.detectChanges();
      }
    });
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(StatusDialogComponent, {
      width: '500px',
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.page.set(0);
        this.loadStatuses();
      }
    });
  }

  openEditDialog(status: CustomStatus): void {
    const dialogRef = this.dialog.open(StatusDialogComponent, {
      width: '500px',
      data: { ...status }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadStatuses();
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onMetaStatusFilterChange(value: MetaStatus | 'ALL'): void {
    this.metaStatusFilter = value;
    this.page.set(0);
    this.loadStatuses();
  }

  onTypeFilterChange(value: StatusType | 'ALL'): void {
    this.typeFilter = value;
    this.page.set(0);
    this.loadStatuses();
  }

  onToggleArchived(): void {
    this.showArchived = !this.showArchived;
    this.page.set(0);
    this.loadStatuses();
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadStatuses();
  }

}
